/**
 * diffEngine.ts
 *
 * Pure, deterministic functions for pricing-change detection and audit diff
 * computation. No UI dependencies, no side effects, no DB calls.
 *
 * This is the core decision engine for Phase 3.
 */

import { runAudit } from '../rules';
import { pricingService } from './pricingService';
import { SUPPORTED_TOOLS } from '../data/pricing';
import {
  StoredAudit,
  AuditResult,
  AuditInput,
  PricingSnapshot,
  PricingChange,
  Recommendation,
  AuditDiff,
  StalenessResult,
  StalenessReason,
} from '../types';

// ─── Thresholds ───────────────────────────────────────────────────────────────
// Conservative, defensible materiality thresholds.
const STALENESS_ABSOLUTE_THRESHOLD_MONTHLY = 5;   // $5/month minimum delta
const STALENESS_RELATIVE_THRESHOLD_PERCENT = 5;    // 5% relative delta

// ─── 1. Load Current Pricing ──────────────────────────────────────────────────

/**
 * Returns the current PricingSnapshot from the repo's pricing data file.
 * This is the single source of truth for "what pricing looks like now."
 */
export function loadCurrentPricing(): PricingSnapshot {
  return pricingService.serializePricingSnapshot(SUPPORTED_TOOLS);
}

// ─── 2. Recompute Audit From Current Pricing ──────────────────────────────────

/**
 * Reruns the deterministic audit engine using the stored tool inputs but
 * updated monthly spends reflecting current pricing definitions.
 *
 * The stored tool inputs (toolId, tier, userCount) are kept intact.
 * Only the monthlySpend is recalculated from current plan prices.
 */
export function recomputeAuditFromSnapshot(storedAudit: StoredAudit): AuditResult {
  const updatedTools = storedAudit.toolsJson.map(toolInput => {
    const currentTool = SUPPORTED_TOOLS.find(t => t.id === toolInput.toolId);
    const plan = currentTool?.plans.find(p => p.id === toolInput.tier);

    // Only override spend for fixed-subscription plans with a known price
    let monthlySpend = toolInput.monthlySpend;
    if (plan && plan.pricingModel === 'subscription' && plan.monthlyPrice !== null) {
      monthlySpend = plan.monthlyPrice * toolInput.userCount;
    }

    return { ...toolInput, monthlySpend };
  });

  const auditInput: AuditInput = {
    teamSize: storedAudit.teamSize,
    useCase: storedAudit.primaryUseCase,
    tools: updatedTools,
  };

  return runAudit(auditInput);
}

// ─── 3. Compare Recommendations ───────────────────────────────────────────────

/**
 * Returns true if any recommendation title or type changed between old and new results.
 * Compares by matching on toolId.
 */
function recommendationsChanged(
  oldRecs: Recommendation[],
  newRecs: Recommendation[],
): { changed: boolean; reason: StalenessReason | null } {
  if (newRecs.length > oldRecs.length) {
    return { changed: true, reason: 'recommendation_added' };
  }
  if (newRecs.length < oldRecs.length) {
    return { changed: true, reason: 'recommendation_removed' };
  }

  for (const oldRec of oldRecs) {
    const matchingNew = newRecs.find(r => r.toolId === oldRec.toolId);
    if (!matchingNew) {
      // Tool still present but recommendation disappeared
      return { changed: true, reason: 'recommendation_removed' };
    }
    if (matchingNew.type !== oldRec.type) {
      return { changed: true, reason: 'recommendation_type_changed' };
    }
    if (matchingNew.title !== oldRec.title) {
      return { changed: true, reason: 'recommendation_title_changed' };
    }
  }

  return { changed: false, reason: null };
}

// ─── 4. Determine Staleness ───────────────────────────────────────────────────

/**
 * Classifies whether a recomputed audit is materially stale compared to the
 * originally stored version. Returns a typed staleness result with reason.
 *
 * Uses conservative thresholds to avoid false positives.
 */
export function determineStaleness(
  storedAudit: StoredAudit,
  newResult: AuditResult,
): StalenessResult {
  const deltaMonthly = newResult.potentialSavings - storedAudit.monthlySavings;
  const deltaAnnual = deltaMonthly * 12;
  const deltaPercent =
    storedAudit.monthlySavings > 0
      ? (Math.abs(deltaMonthly) / storedAudit.monthlySavings) * 100
      : Math.abs(deltaMonthly) > 0
      ? 100
      : 0;

  // Check savings delta threshold first (most common trigger)
  const savingsDeltaSignificant =
    Math.abs(deltaMonthly) >= STALENESS_ABSOLUTE_THRESHOLD_MONTHLY ||
    deltaPercent >= STALENESS_RELATIVE_THRESHOLD_PERCENT;

  if (savingsDeltaSignificant) {
    return {
      isStale: true,
      reason: 'savings_delta_significant',
      deltaMonthly,
      deltaAnnual,
      deltaPercent,
    };
  }

  // Check recommendation structure changes
  const { changed, reason } = recommendationsChanged(
    storedAudit.recommendationsJson,
    newResult.recommendations,
  );

  if (changed && reason) {
    return { isStale: true, reason, deltaMonthly, deltaAnnual, deltaPercent };
  }

  return {
    isStale: false,
    reason: 'not_stale' as StalenessReason,
    deltaMonthly,
    deltaAnnual,
    deltaPercent,
  };
}

// ─── 5. Build Audit Diff ──────────────────────────────────────────────────────

/**
 * Assembles the full AuditDiff object - the single source of truth for
 * notification emails, re-audit UI, and debug output.
 */
export function buildAuditDiff(
  storedAudit: StoredAudit,
  newResult: AuditResult,
  pricingChanges: PricingChange[],
  userEmail?: string,
): AuditDiff {
  const staleness = determineStaleness(storedAudit, newResult);
  const deltaMonthlySavings = staleness.deltaMonthly;
  const deltaAnnualSavings = staleness.deltaAnnual;

  // Filter pricing changes to only tools present in this specific audit
  const changedTools = pricingChanges.filter(change =>
    storedAudit.toolsJson.some(t => t.toolId === change.toolId),
  );

  const changeSummary = buildChangeSummary(storedAudit, staleness, changedTools);

  return {
    auditId: storedAudit.id,
    publicId: storedAudit.publicId,
    userEmail,
    createdAt: storedAudit.createdAt,
    oldMonthlySpend: storedAudit.totalMonthlySpend,
    newMonthlySpend: newResult.totalMonthlySpend,
    oldMonthlySavings: storedAudit.monthlySavings,
    newMonthlySavings: newResult.potentialSavings,
    oldAnnualSavings: storedAudit.annualSavings,
    newAnnualSavings: newResult.potentialSavings * 12,
    deltaMonthlySavings,
    deltaAnnualSavings,
    oldRecommendations: storedAudit.recommendationsJson,
    newRecommendations: newResult.recommendations,
    changedTools,
    staleFlag: staleness.isStale,
    stalenessReason: staleness.reason,
    changeSummary,
  };
}

// ─── 6. Compare Audit Results ─────────────────────────────────────────────────

/**
 * High-level function: given a stored audit and a newly computed result,
 * returns both the pricing changes and the full diff object.
 */
export function compareAuditResults(
  storedAudit: StoredAudit,
  newResult: AuditResult,
  userEmail?: string,
): AuditDiff {
  // Compute pricing changes between stored snapshot and current
  const currentSnapshot = loadCurrentPricing();
  const pricingChanges: PricingChange[] = storedAudit.pricingSnapshotJson
    ? pricingService.computePricingChanges(storedAudit.pricingSnapshotJson, currentSnapshot)
    : [];

  return buildAuditDiff(storedAudit, newResult, pricingChanges, userEmail);
}

// ─── Helper: Build Change Summary ─────────────────────────────────────────────

function buildChangeSummary(
  storedAudit: StoredAudit,
  staleness: StalenessResult,
  changedTools: PricingChange[],
): string {
  if (!staleness.isStale) {
    return 'No material changes detected. Audit is current.';
  }

  const lines: string[] = [];

  if (staleness.reason === 'savings_delta_significant') {
    const sign = staleness.deltaMonthly >= 0 ? '+' : '';
    lines.push(
      `Potential savings shifted by ${sign}$${staleness.deltaMonthly.toFixed(2)}/month` +
        ` (${sign}${staleness.deltaPercent.toFixed(1)}% change).`,
    );
  } else if (staleness.reason === 'recommendation_added') {
    lines.push('A new recommendation became applicable based on current pricing.');
  } else if (staleness.reason === 'recommendation_removed') {
    lines.push('A previous recommendation is no longer applicable.');
  } else if (staleness.reason === 'recommendation_type_changed') {
    lines.push('The category of an existing recommendation has changed.');
  } else if (staleness.reason === 'recommendation_title_changed') {
    lines.push('The action for an existing recommendation has changed.');
  }

  if (changedTools.length > 0) {
    const toolNames = [...new Set(changedTools.map(c => c.toolName))].join(', ');
    lines.push(`Affected tools: ${toolNames}.`);
  }

  lines.push(`Audit created: ${storedAudit.createdAt}.`);
  return lines.join(' ');
}
